import { InputAdornment, OutlinedInput, Stack, Button, Typography, Grid } from "@mui/material";
import React from "react";
import ListPageHeader, {
  ListPageHeaderLeft,
  ListPageHeaderRight,
} from "../../../../../../Components/Misc/ListPageHeader";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import SquareCard from "../../../../../../Components/Cards/SquareCard";
import { useMultipleSelection } from "../../../../../../Utils/Hooks/useMultipleSelection";
import { routePaths } from "../../../../../../Router/router";
import { ModalContext } from "../../../../../../Components/Modals";
import ModalCreatePack, {
  ModalCreatePackElementProps,
} from "../../../../../../Components/Modals/ModalCreatePack/modal-ingest-config-pack-create";
import { useEffect, useState } from "react";
import { ingestService } from "../../../../../../Core/Api/services";
import { DbIngestConfigPackInfos } from "../../../../../../Core/Api/Types/db-ingest-config-pack-infos";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import useCustomSnackbar from "../../../../../../Utils/Hooks/useCustomSnackbar";
import ModalCreateOrUpdatePlaceholder, {
  IngestConfigurePlaceholderContext,
  ModalCreatePlaceholderProps,
} from "../../../../../../Components/Modals/ModalCreateOrUpdatePlaceholder/modal-ingest-config-placeholder-create-or-update";
import { UserHasAcl } from "../../../../../../Utils/helpers";
import { AclTypes } from "../../../../../../Core/Api/Types/acl-types";
import { useTranslation } from "react-i18next";

function IngestPacksIndex() {
  const { t } = useTranslation();
  const [packs, setPacks] = useState<DbIngestConfigPackInfos[]>([]);
  const [filterText, setFilterText] = useState("");
  const [iterate, setIterate] = useState(0);
  const { selected, select, isSelected } = useMultipleSelection<number>();

  const selectOnClick = React.useMemo(() => selected.length > 0, [selected]);

  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const enqueueCustomSnackbar = useCustomSnackbar();

  const onClickNewPack = () => {
    if (!openModal) {
      return;
    }

    const onClose: ModalCreatePackElementProps["onClose"] = (data) => {
      setIterate((prev) => ++prev);
    };

    openModal<ModalCreatePackElementProps>(ModalCreatePack, {
      onClose,
    });
  };

  const onClickDelete = (id: number) => {
    if (!openModal) {
      return;
    }

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: false,
      commentLabel: t("modal:comment.confirmDeletePack"),
      title: t("modal:title.confirmDelete"),
      onConfirm: () => {
        ingestService.DeleteConfigPackById(id).then((response) => {
          setIterate((prev) => ++prev);
          enqueueCustomSnackbar(t("alert:success.packWithIdDeleted", { id }), "success");
        });
      },
    });
  };

  const onClickCreatePlaceholder = () => {
    if (!openModal) return;
    openModal<ModalCreatePlaceholderProps>(ModalCreateOrUpdatePlaceholder, {
      context: IngestConfigurePlaceholderContext.Create,
      closeModal: () => false,
    });
  };

  useEffect(() => {
    ingestService.GetConfigPackList().then((response) => {
      if (response.packs) setPacks(response.packs);
    });
  }, [iterate]);

  return (
    <>
      <ListPageHeader mb={3}>
        <ListPageHeaderLeft>
          <Typography variant="h5">{`${packs.length} pack${packs.length > 1 ? "s" : ""}`}</Typography>
        </ListPageHeaderLeft>
        <ListPageHeaderRight>
          <OutlinedInput
            label={t("common:standard")}
            placeholder={t("common:search")}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            }
          />
          {UserHasAcl(AclTypes.aclIngestConfigCreation) && (
            <Button startIcon={<AddIcon />} color="primary" variant="contained" onClick={onClickNewPack}>
              {t("button:newPack")}
            </Button>
          )}
          {UserHasAcl(AclTypes.aclIngestPlaceholderCreate) && (
            <Button startIcon={<AddIcon />} color="primary" variant="contained" onClick={onClickCreatePlaceholder}>
              {t("button:newPlaceholder")}
            </Button>
          )}
        </ListPageHeaderRight>
      </ListPageHeader>
      <Grid container spacing={2}>
        {packs
          .filter((x) => x.name?.includesAllWords(filterText))
          .map((pack, index) => (
            <Grid item xs={3} lg={2} key={index}>
              <SquareCard
                id={pack.id}
                title={pack.name!}
                to={`${routePaths.ingestPacksSingle.path}/${pack.id}`}
                subtitle={`${pack.description}`}
                onCheckboxClick={(id) => select(id)}
                onEditClick={() => false}
                onDeleteClick={(id) => onClickDelete(id)}
                selected={isSelected(pack.id)}
                selectOnClick={selectOnClick}
                selectMode={false}
                maskEdit={true}
              />
            </Grid>
          ))}
      </Grid>
    </>
  );
}

export default IngestPacksIndex;
